import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Minus, Plus } from 'lucide-react'
import type { TicketTypeDocument } from '@/types'
import type { CheckoutItem } from '../types'

export function formatPrice(cents: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100)
}

function formatDate(ts: import('firebase/firestore').Timestamp): string {
  return ts.toDate().toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

type SaleStatus = 'upcoming' | 'on_sale' | 'ended' | 'sold_out'

function getSaleStatus(tt: TicketTypeDocument, sharedRemaining: number | null): SaleStatus {
  const now = Date.now()
  const typeRemaining = tt.capacity - tt.sold - (tt.reserved ?? 0)
  const remaining =
    sharedRemaining === null ? typeRemaining : Math.min(typeRemaining, sharedRemaining)
  if (remaining <= 0) return 'sold_out'
  if (now < tt.saleStartsAt.toMillis()) return 'upcoming'
  if (now > tt.saleEndsAt.toMillis()) return 'ended'
  return 'on_sale'
}

interface Props {
  ticketTypes: Array<TicketTypeDocument & { id: string }>
  sharedCapacity?: number
  sharedRemainingSeats?: number | null
  value: CheckoutItem[]
  onChange: (items: CheckoutItem[]) => void
}

export function TicketTypeSelector({
  ticketTypes,
  sharedCapacity,
  sharedRemainingSeats,
  value,
  onChange,
}: Props) {
  const sharedRemaining =
    sharedCapacity === undefined
      ? null
      : Math.max(0, sharedRemainingSeats ?? 0)
  const selectedTotal = value.reduce((total, item) => total + item.quantity, 0)

  function getQuantity(ticketTypeId: string): number {
    return value.find((i) => i.ticketTypeId === ticketTypeId)?.quantity ?? 0
  }

  function setQuantity(tt: TicketTypeDocument & { id: string }, qty: number) {
    const next = value.filter((i) => i.ticketTypeId !== tt.id)
    if (qty > 0) {
      next.push({
        ticketTypeId: tt.id,
        ticketTypeName: tt.name,
        quantity: qty,
        unitPrice: tt.price,
      })
    }
    onChange(next)
  }

  if (ticketTypes.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">No tickets available for this event.</p>
    )
  }

  return (
    <div className="space-y-3">
      {sharedCapacity !== undefined && (
        <div className="rounded-lg border bg-muted/30 p-3 text-sm">
          <p className="font-medium">Shared show capacity: {sharedCapacity} seats</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Every ticket option below draws from the same physical seat pool.
          </p>
        </div>
      )}
      {ticketTypes.map((tt) => {
        const qty = getQuantity(tt.id)
        const status = getSaleStatus(tt, sharedRemaining)
        const typeRemaining = tt.capacity - tt.sold - (tt.reserved ?? 0)
        const remaining =
          sharedRemaining === null ? typeRemaining : Math.min(typeRemaining, sharedRemaining)
        const availableForThisSelection =
          sharedRemaining === null ? remaining : sharedRemaining - (selectedTotal - qty)
        const maxQty = Math.min(10, remaining, Math.max(0, availableForThisSelection))
        const disabled = status !== 'on_sale'

        return (
          <div
            key={tt.id}
            className={`rounded-lg border p-4 ${disabled ? 'opacity-60' : ''}`}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 space-y-1">
                <div className="flex items-center gap-2">
                  <p className="font-medium">{tt.name}</p>
                  {status === 'upcoming' && (
                    <Badge variant="secondary">Upcoming</Badge>
                  )}
                  {status === 'ended' && (
                    <Badge variant="secondary">Sale Ended</Badge>
                  )}
                  {status === 'sold_out' && (
                    <Badge variant="destructive">Sold Out</Badge>
                  )}
                  {status === 'on_sale' && (
                    <Badge variant="default">On Sale</Badge>
                  )}
                </div>
                {tt.description && (
                  <p className="text-sm text-muted-foreground">{tt.description}</p>
                )}
                <p className="text-sm font-semibold">{formatPrice(tt.price)}</p>
                {status === 'upcoming' && (
                  <p className="text-xs text-muted-foreground">
                    Sale opens {formatDate(tt.saleStartsAt)}
                  </p>
                )}
                {status === 'ended' && (
                  <p className="text-xs text-muted-foreground">
                    Sale closed {formatDate(tt.saleEndsAt)}
                  </p>
                )}
                {status === 'on_sale' && (
                  <p className="text-xs text-muted-foreground">
                    {sharedRemaining !== null ? 'Up to ' : ''}{remaining} remaining
                    {sharedRemaining !== null ? ' in this show' : ''} · Sale ends{' '}
                    {formatDate(tt.saleEndsAt)}
                  </p>
                )}
              </div>

              {status === 'on_sale' && (
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    disabled={qty === 0}
                    onClick={() => setQuantity(tt, qty - 1)}
                  >
                    <Minus className="h-3 w-3" />
                  </Button>
                  <span className="w-6 text-center text-sm font-medium">{qty}</span>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    disabled={qty >= maxQty}
                    onClick={() => setQuantity(tt, qty + 1)}
                  >
                    <Plus className="h-3 w-3" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}
